import type { ConditionId } from 'lib/data/do-i-qualify-states';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';

import { Button, Logo, SelectFieldMinimal } from '@leafwell/components';

import { useStatesList } from 'hooks/useStatesList';
import { useEventsTracker } from 'contexts/eventsTracker';

import {
  getStateConditions,
  getStateAgeRange,
  getStateConfig,
  getConditionLabel,
  shouldShowConditionsStep,
  isSupportedState,
} from 'lib/data/do-i-qualify-states';
import {
  getQualifyResult,
  getMatrixCondition,
  getStateTemplate,
  UNDER_18_NOTE,
} from 'lib/data/do-i-qualify-matrix';
import SignupFormSimple from './signup-form-simple';
import { useWordPressContext } from 'contexts/wordpress-provider';
import SignupFormPasswordless from './signup-form-passwordless';

type Step = 'state' | 'age' | 'conditions' | 'results';

type DoIQualifyProps = {
  /**
   * The state this page is about. Pass it on state pages to skip the selector.
   * OMIT on secondary pages (/blog, /medical-conditions) — the modal will ask,
   * pre-filled from the state cookie / geolocation.
   */
  currentState?: string;
  className?: string;
  promoCode?: string;
  label?: string;
  description?: string;
  showIcon?: boolean;
  variant?: 'white' | 'green' | null;
  passwordless?: boolean;
};

const DoIQualify: React.FC<DoIQualifyProps> = ({
  currentState,
  className,
  promoCode = 'QUALIFY10',
  label,
  description,
  showIcon = true,
  variant = 'white',
  passwordless = false,
}) => {
  const { trackEvent } = useEventsTracker();

  const statesList = useStatesList(false);

  const needsStateStep = !currentState;

  const [selectedState, setSelectedState] = useState<string>('');
  const effectiveState = currentState || selectedState;

  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<Step>(needsStateStep ? 'state' : 'age');
  const [age, setAge] = useState<string | null>(null);
  const [conds, setConds] = useState<ConditionId[]>([]);

  const { statePages } = useWordPressContext();
  const waitlistUrl = statePages.find(
    ({ code }) => code === effectiveState,
  )?.uri;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollHint, setShowScrollHint] = useState(false);

  const stateConfig = getStateConfig(effectiveState);
  const stateName = stateConfig?.name ?? '';
  const stateSupported = isSupportedState(effectiveState);
  const conditions = getStateConditions(effectiveState);
  const ageOptions = getStateAgeRange(effectiveState);

  const questionSteps = useMemo<Step[]>(
    () => [
      ...(needsStateStep ? (['state'] as Step[]) : []),
      'age',
      ...(shouldShowConditionsStep(effectiveState)
        ? (['conditions'] as Step[])
        : []),
    ],
    [needsStateStep, effectiveState],
  );

  const flow = useMemo<Step[]>(
    () => [...questionSteps, 'results'],
    [questionSteps],
  );

  const stepNumber = questionSteps.indexOf(step) + 1;
  const totalQuestions = questionSteps.length;
  const progress = ((flow.indexOf(step) + 1) / flow.length) * 100;
  const isFirstStep = flow.indexOf(step) === 0;

  const goNext = () => setStep(flow[flow.indexOf(step) + 1]);
  const goBack = () => setStep(flow[flow.indexOf(step) - 1]);

  // Lock body scroll + hide the sticky header while the modal is open.
  useEffect(() => {
    const header = document.querySelector('#header-container');

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      header?.classList.add('!static');
      header?.classList.add('!hidden');
    } else {
      document.body.style.overflow = '';
      header?.classList.remove('!static');
      header?.classList.remove('!hidden');
    }

    return () => {
      document.body.style.overflow = '';
      header?.classList.remove('!static');
      header?.classList.remove('!hidden');
    };
  }, [isOpen]);

  // Scroll-hint indicator for the (potentially long) conditions grid.
  const checkOverflow = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const hasOverflow = el.scrollHeight > el.clientHeight + 1;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 16;
    setShowScrollHint(hasOverflow && !atBottom);
  };

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!isOpen || step !== 'conditions' || !el) {
      setShowScrollHint(false);
      return;
    }

    checkOverflow();

    const resizeObserver = new ResizeObserver(() => checkOverflow());
    resizeObserver.observe(el);

    let scrollTimeout: ReturnType<typeof setTimeout>;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(checkOverflow, 50);
    };
    el.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      resizeObserver.disconnect();
      el.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [isOpen, step, conds, effectiveState]);

  const buttonTitle = label ?? 'Check if you qualify';
  const buttonClass =
    variant === 'green'
      ? 'relative select-none cursor-pointer whitespace-nowrap inline-grid gap-x-2 items-center justify-center text-center text-lg font-semibold transition-colors focus:outline-none outline-none cursor-pointer leading-none border rounded-md min-h-50 py-3 px-6 bg-green-600 border-green-600 text-white hover:border-green-800 hover:bg-green-700 hover:text-white'
      : 'px-4 py-3 bg-white border border-black rounded-md font-medium hover:bg-lilac-300 hover:border-lilac-300 transition-colors';

  const openModal = () => {
    trackEvent({
      event: 'select_content',
      content_type: buttonTitle,
      item_id: 'do_i_qualify_button',
      action: 'open_do_i_qualify_modal',
      state_code: effectiveState,
      has_state_step: needsStateStep,
      origin: window?.location?.pathname,
    });
    setStep(needsStateStep ? 'state' : 'age');
    setSelectedState(currentState || '');
    setAge(null);
    setConds([]);
    setIsOpen(true);
  };

  const closeModal = () => {
    trackEvent({
      event: 'select_content',
      content_type: buttonTitle,
      item_id: 'do_i_qualify_button',
      action: 'close_do_i_qualify_modal',
      origin: window?.location?.pathname,
    });
    setIsOpen(false);
  };

  const toggleCondition = (id: ConditionId) =>
    setConds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    );

  const goToResults = () => {
    trackEvent({
      event: 'select_content',
      content_type: buttonTitle,
      item_id: 'do_i_qualify_result',
      action: 'view_do_i_qualify_result',
      state_code: effectiveState,
      copy_template: getStateTemplate(effectiveState),
      age_range: age,
      conditions_count: conds.length,
      primary_condition: getMatrixCondition(conds),
      origin: window?.location?.pathname,
    });
    setStep('results');
  };

  const result =
    step === 'results'
      ? getQualifyResult({
          stateCode: effectiveState,
          ageRange: age,
          conditionIds: conds,
          promoCode,
        })
      : null;

  const stepLabel = (
    <p className="text-xs font-bold uppercase tracking-wide mb-2">
      <span className="text-green-500">Step {stepNumber}</span>{' '}
      <span className="text-black">of {totalQuestions}</span>
    </p>
  );

  const backButton = (
    <button
      onClick={goBack}
      className="inline-flex items-center gap-x-1 text-sm font-medium uppercase text-gray-400 hover:text-gray-700 mb-4"
    >
      ← Back
    </button>
  );

  const introLine = (
    <p className="text-sm font-medium text-purple-500 mb-4">
      Find out if you qualify in {totalQuestions} quick question
      {totalQuestions === 1 ? '' : 's'}
    </p>
  );

  return (
    <>
      {/* Hero trigger button */}
      <button onClick={openModal} className={`${buttonClass} ${className}`}>
        <div className="flex items-center justify-center gap-x-2">
          {showIcon && (
            <Image
              src="/question-mark-icon.svg"
              alt=""
              width={20}
              height={20}
            />
          )}
          {buttonTitle}
        </div>

        {description && (
          <div
            className={`${
              variant === 'green' ? 'text-white' : 'text-black'
            } text-xs font-semibold mx-auto mt-1`}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}
      </button>

      {isOpen && (
        <div className="fixed left-0 right-0 bottom-0 top-0 z-50 flex items-start lg:items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute left-0 right-0 bottom-0 -top-32 bg-black/40"
            onClick={closeModal}
          />

          {/* Modal */}
          <div
            className={`relative z-10 bg-white rounded-none lg:rounded-2xl shadow-xl w-full h-full max-h-full lg:max-h-[85vh] max-w-md lg:max-w-xl mx-4 flex flex-col ${
              step === 'state' ? 'lg:h-[640px]' : 'lg:h-auto'
            }`}
          >
            {/* Header */}
            <div className="flex items-center justify-between py-4 px-6 bg-lilac-50 rounded-t-none lg:rounded-t-2xl flex-shrink-0">
              <Logo classNameButton="w-32" />
              <button
                onClick={closeModal}
                className="flex items-center gap-x-2 text-sm font-medium"
              >
                CLOSE{' '}
                <Image src="/close.svg" alt="Close" width={16} height={16} />
              </button>
            </div>

            {/* Progress bar */}
            <div className="h-1 bg-lilac-50 flex-shrink-0">
              <div
                className="h-full bg-green-500 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>

            {/* Body */}
            <div className="relative flex-1 overflow-y-auto px-6 py-6 flex flex-col not-prose">
              {/* STEP — State selector (secondary pages only) */}
              {step === 'state' && (
                <div className="flex flex-col flex-1">
                  {introLine}
                  {stepLabel}
                  <h2 className="text-xl font-semibold text-black mb-1">
                    Confirm the state you&apos;re applying in
                  </h2>
                  <p className="text-sm text-gray-500 mb-5">
                    Select the state where you&apos;re seeking certification.
                  </p>

                  <div className="grid gap-y-2 js-form-field relative mb-4">
                    <p className="font-medium">You&apos;re applying for</p>
                    <SelectFieldMinimal
                      aria-label="Select your state"
                      placeholder="Select your state"
                      options={statesList}
                      disabled={false}
                      onSelect={target => {
                        setSelectedState(target.value);
                        // A different state means a different condition list
                        // and possibly different age buckets.
                        setConds([]);
                        setAge(null);
                        trackEvent({
                          event: 'select_content',
                          content_type: buttonTitle,
                          item_id: 'do_i_qualify_state_select',
                          action: 'select_state',
                          state_code: target.value,
                          state_supported: isSupportedState(target.value),
                          origin: window?.location?.pathname,
                        });
                      }}
                    />
                  </div>

                  {/* Leafwell doesn't operate here → waitlist */}
                  {selectedState && !stateSupported ? (
                    <div className="bg-[#FFF8F0] border border-[#FDDBB0] rounded-xl px-4 py-3 text-sm text-[#665544] leading-relaxed mb-4">
                      Leafwell isn&apos;t available in this state yet.{' '}
                      <a
                        href={waitlistUrl}
                        className="font-semibold underline text-purple-600"
                        onClick={() =>
                          trackEvent({
                            event: 'select_content',
                            content_type: buttonTitle,
                            item_id: 'do_i_qualify_waitlist_link',
                            action: 'click_waitlist',
                            state_code: selectedState,
                            origin: window?.location?.pathname,
                          })
                        }
                      >
                        Join the waiting list
                      </a>{' '}
                      and we&apos;ll let you know as soon as we launch there.
                    </div>
                  ) : null}

                  <Button
                    label="Continue"
                    rounded
                    variant="primary"
                    filled
                    disabled={!stateSupported}
                    className="w-full mt-auto"
                    onClick={goNext}
                  />
                </div>
              )}

              {/* STEP — Age group */}
              {step === 'age' && (
                <>
                  {isFirstStep ? introLine : backButton}
                  {stepLabel}
                  <h2 className="text-xl font-semibold text-black mb-1">
                    Let&apos;s start with your age.
                  </h2>
                  <p className="text-sm text-gray-500 mb-5">
                    Medical cannabis is available to adults, and to minors with
                    a caregiver.
                  </p>

                  <div className="flex flex-col gap-y-2 mb-4">
                    {ageOptions.map(option => {
                      const selected = age === option;
                      return (
                        <button
                          key={option}
                          onClick={() => setAge(option)}
                          className={`flex items-center gap-x-3 w-full px-4 py-3 border-2 rounded-xl text-left text-sm font-semibold transition-colors ${
                            selected
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-lilac-300 hover:bg-lilac-50'
                          }`}
                        >
                          <span
                            className={`w-5 h-5 flex-shrink-0 rounded-md border-2 flex items-center justify-center ${
                              selected
                                ? 'bg-green-500 border-green-500'
                                : 'border-gray-300'
                            }`}
                          >
                            {selected ? (
                              <Image
                                src="/check.svg"
                                alt=""
                                width={11}
                                height={11}
                              />
                            ) : null}
                          </span>
                          {option}
                        </button>
                      );
                    })}
                  </div>

                  {age === 'Under 18' ? (
                    <div className="bg-[#FFF8F0] border border-[#FDDBB0] rounded-xl px-4 py-3 text-sm text-[#665544] leading-relaxed mb-4">
                      {UNDER_18_NOTE.replace(
                        /\{state\}/g,
                        stateName || 'your state',
                      )}
                    </div>
                  ) : null}

                  <Button
                    label="Continue"
                    rounded
                    variant="primary"
                    filled
                    disabled={age === null}
                    className="w-full mt-auto"
                    onClick={goNext}
                  />
                </>
              )}

              {/* STEP — Conditions */}
              {step === 'conditions' && (
                <>
                  {isFirstStep ? introLine : backButton}
                  {stepLabel}
                  <h2 className="text-xl font-semibold text-black mb-1">
                    What are you seeking care for?
                  </h2>
                  <p className="text-sm text-gray-500 mb-4">
                    Select all that apply — qualifying conditions in{' '}
                    {stateName || 'your state'}. Not sure? A Leafwell doctor can
                    help determine your eligibility.
                  </p>

                  <div
                    ref={scrollContainerRef}
                    className="grid grid-cols-2 gap-2 mb-1 flex-1 min-h-0 overflow-y-auto pr-1"
                  >
                    {conditions.map(entry => {
                      const selected = conds.includes(entry.id);
                      return (
                        <button
                          key={entry.id}
                          onClick={() => toggleCondition(entry.id)}
                          title={entry.note}
                          className={`px-3 py-2 border-2 rounded-lg text-left text-sm font-semibold leading-tight transition-colors ${
                            selected
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-lilac-300 hover:bg-lilac-50'
                          }`}
                        >
                          {getConditionLabel(entry)}
                        </button>
                      );
                    })}
                  </div>

                  {showScrollHint ? (
                    <button
                      type="button"
                      onClick={() => {
                        scrollContainerRef.current?.scrollBy({
                          top: 200,
                          behavior: 'smooth',
                        });
                      }}
                      className="flex justify-center -mt-3 mb-1 cursor-pointer"
                    >
                      <span className="text-white text-2xl font-bold animate-bounce h-6 w-6 rounded-full flex items-center justify-center bg-purple-500">
                        <Image
                          src="/caret-white.svg"
                          alt="Scroll down"
                          width={12}
                          height={12}
                        />
                      </span>
                    </button>
                  ) : null}

                  <Button
                    label="Continue"
                    rounded
                    variant="primary"
                    filled
                    disabled={conds.length === 0}
                    className="w-full mt-4"
                    onClick={goToResults}
                  />
                  <button
                    onClick={goToResults}
                    className="block w-full text-center mt-3 px-4 py-3 border border-black rounded-md text-sm font-semibold text-black hover:bg-lilac-50 transition-colors"
                  >
                    Skip - I&apos;ll discuss with a doctor
                  </button>
                </>
              )}

              {/* STEP — Results */}
              {step === 'results' && result ? (
                <>
                  <div className="bg-[#F6FEF9] rounded-xl px-6 py-6 text-center mb-5">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white mb-2">
                      <Image
                        src="/certified-green.svg"
                        alt=""
                        width={40}
                        height={63}
                      />
                    </div>
                    <h2 className="text-2xl font-bold text-black leading-tight tracking-tight">
                      {result.headline}
                    </h2>
                    <p className="text-base text-green-500 font-semibold mt-0.5">
                      {result.subheadline}
                    </p>
                  </div>

                  <p className="text-center text-sm mb-5">{result.body}</p>

                  {/* What happens next? */}
                  <p className="text-base font-bold text-black mb-3">
                    What happens next?
                  </p>
                  <ol className="flex flex-col gap-y-3 mb-5">
                    {result.nextSteps.map((next, index) => (
                      <li key={next.title} className="flex gap-x-3">
                        <span className="w-5 h-5 mt-0.5 flex-shrink-0 rounded-full bg-green-500 text-white text-[11px] font-bold flex items-center justify-center">
                          {index + 1}
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-black leading-tight">
                            {next.title}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {next.subtitle}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ol>

                  {/* Promo + CTA */}
                  <div className="border border-green-500 bg-[#F6FEF9] rounded-xl p-4 mt-auto">
                    <div className="flex items-center justify-between gap-x-3">
                      <p className="text-xs font-extrabold uppercase tracking-wide text-purple-500 leading-tight max-w-[65%]">
                        Your exclusive discount
                      </p>
                      <p className="text-lg font-bold text-green-500 leading-none">
                        10% OFF
                      </p>
                    </div>
                    {passwordless ? (
                      <SignupFormPasswordless
                        className="w-full !text-base !px-0"
                        stateCode={effectiveState}
                        promoCode={promoCode}
                        formSubmitText="Book a consultation"
                        ctaLocation="do-i-qualify-modal"
                        showBorder={false}
                        showStateSelect={false}
                      />
                    ) : (
                      <SignupFormSimple
                        className="w-full !text-base !px-0"
                        stateCode={effectiveState}
                        promoCode={promoCode}
                        formSubmitText="Book a consultation"
                        ctaLocation="do-i-qualify-modal"
                        showBorder={false}
                        showStateSelect={false}
                      />
                    )}
                  </div>

                  <button
                    onClick={closeModal}
                    className="block w-full text-center mt-3 text-sm text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    Maybe later
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DoIQualify;
